import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, User, Mail, Phone, Building2, Store,
  FileText, Loader2, KeyRound, Lock, Eye, EyeOff, CheckCircle2, ShieldAlert,
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { useToast } from '../components/Toast';
import { formatPhoneAsYouType, parseInternationalPhone, type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';

interface EmployeeFormData {
  firstName: string;
  lastName: string;
  code: string;
  email: string;
  phone: string;
  companyId: string;
  shopIds: string[];
  type: 'VENDOR' | 'SECRETARY' | 'OTHER';
  notes: string;
  isActive: boolean;
}

export function EmployeeEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showToast, ToastComponent } = useToast();
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage({ requireSingleShop: true });

  const [formData, setFormData] = useState<EmployeeFormData>({
    firstName: '', lastName: '', code: '', email: '', phone: '',
    companyId: '', shopIds: [], type: 'OTHER', notes: '', isActive: true,
  });

  const [loading,  setLoading]  = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [shops,     setShops]     = useState<Array<{ id: string; name: string }>>([]);

  // User-account state
  const [hasUserAccount,  setHasUserAccount]  = useState<boolean | null>(null);
  const [linkedUserEmail, setLinkedUserEmail] = useState<string>('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword,    setShowPassword]    = useState(false);
  const [creatingUser,    setCreatingUser]    = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const set = (field: keyof EmployeeFormData, value: any) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!id || !canLoad) return;

    Promise.all([
      apiClient(`/api/admin/employees/${id}`).then((r) => r.json()),
      apiClient('/api/admin/companies?limit=200').then((r) => r.json()),
      apiClient('/api/admin/shops?limit=200').then((r) => r.json()),
    ])
      .then(([empData, compData, shopData]) => {
        if (!empData.success) { navigate('/employees'); return; }
        const employee = empData.data.employee;
        const rawPhone = employee.person?.phone || '';
        const parsedPhone = parseInternationalPhone(rawPhone, 'FR');
        const nextPhoneCountry = parsedPhone.country ?? 'FR';
        const nextPhoneDisplay = formatPhoneAsYouType(rawPhone, nextPhoneCountry);
        setPhoneCountry(nextPhoneCountry);
        setPhoneDisplay(nextPhoneDisplay);
        setPhoneE164(parsedPhone.isValid ? parsedPhone.e164 : null);
        setPhoneIsValid(parsedPhone.isValid);
        const linkedShopIds = Array.isArray(employee.employeeShops)
          ? employee.employeeShops.map((es: any) => es.shopId)
          : [];
        const allowedShopIds = currentShopId
          ? linkedShopIds.filter((shopId: string | null | undefined) => shopId === currentShopId)
          : linkedShopIds;
        setFormData({
          firstName: employee.person?.firstName || '',
          lastName:  employee.person?.lastName  || '',
          code:      employee.person?.code || '',
          email:     employee.person?.email     || '',
          phone:     nextPhoneDisplay,
          companyId: employee.companyId || '',
          shopIds:   allowedShopIds.length > 0 ? allowedShopIds : (currentShopId ? [currentShopId] : (employee.shopId ? [employee.shopId] : [])),
          type:      employee.type || 'OTHER',
          notes:     employee.notes || '',
          isActive:  employee.isActive ?? true,
        });
        setHasUserAccount(!!employee.userId);
        setLinkedUserEmail(employee.user?.email || employee.person?.email || '');

        if (compData.success) setCompanies((compData.data?.companies || []).map((c: any) => ({ id: c.id, name: c.name })));
        if (shopData.success) setShops((shopData.data?.shops || []).map((s: any) => ({ id: s.id, name: s.name })));
      })
      .catch((err) => { console.error(err); navigate('/employees'); })
      .finally(() => setFetching(false));
  }, [id, navigate, canLoad, currentShopId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Prénom, nom et email sont obligatoires.');
      return;
    }
    if (!/^[A-Z0-9]{4}$/.test(formData.code.trim().toUpperCase())) {
      setError('Le code (4 caractères alphanumériques) est obligatoire.');
      return;
    }
    if (phoneDisplay.trim() && (!phoneIsValid || !phoneE164)) {
      setError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    try {
      setLoading(true);
      const safeShopIds = currentShopId ? [currentShopId] : formData.shopIds.filter(Boolean);
      const response = await apiClient(`/api/admin/employees/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...formData,
          code: formData.code.trim().toUpperCase(),
          phone: phoneE164 || undefined,
          companyId: formData.companyId || null,
          shopId:    currentShopId || formData.shopIds[0] || null,
          shopIds:   safeShopIds,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const reason = data?.message || 'Erreur lors de la mise à jour';
        setError(reason);
        showToast(reason, 'error', 7000);
        return;
      }
      showToast('Employé mis à jour', 'success');
      navigate('/employees');
    } catch {
      setError('Erreur de connexion au serveur');
      showToast('Erreur de connexion au serveur', 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateUserError(null);
    if (!newPassword) { setCreateUserError('Le mot de passe est obligatoire.'); return; }
    if (newPassword.length < 6) { setCreateUserError('Minimum 6 caractères.'); return; }
    if (newPassword !== confirmPassword) { setCreateUserError('Les mots de passe ne correspondent pas.'); return; }

    try {
      setCreatingUser(true);
      const response = await apiClient(`/api/admin/employees/${id}/create-user`, {
        method: 'POST',
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        setCreateUserError(data?.message || 'Impossible de créer le compte.');
        return;
      }
      setHasUserAccount(true);
      setLinkedUserEmail(data.data?.email || formData.email);
      setNewPassword('');
      setConfirmPassword('');
      showToast(`Compte créé — l'employé peut se connecter avec ${data.data?.email}`, 'success', 5000);
    } catch {
      setCreateUserError('Erreur de connexion au serveur.');
    } finally {
      setCreatingUser(false);
    }
  };

  if (needsShopSelection) {
    return <ShopSelectionGate requireSingleShop />;
  }

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/employees')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Modifier l'employé</h1>
            <p className="text-textSecondary">
              {formData.firstName} {formData.lastName}
            </p>
          </div>
        </div>
        <button
          type="submit"
          form="employee-form"
          disabled={loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Enregistrement...' : 'Mettre à jour'}</span>
        </button>
      </div>

      <ToastComponent />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      <div className="space-y-8">

        <form id="employee-form" onSubmit={handleSubmit} autoComplete="off" className="space-y-8">
          {/* Personal info */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Informations personnelles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Prénom *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Nom *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => set('code', e.target.value.toUpperCase())}
                  className="input-field"
                  maxLength={4}
                  placeholder="JD01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => set('email', e.target.value)}
                    className="input-field pl-9"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Téléphone</label>
                <InternationalPhoneInput
                  value={phoneDisplay}
                  country={phoneCountry}
                  onChange={(result) => {
                    setPhoneDisplay(result.displayValue);
                    setPhoneCountry(result.country);
                    setPhoneE164(result.e164Value);
                    setPhoneIsValid(result.isValid);
                    set('phone', result.displayValue);
                    if (result.isValid) {
                      setError((prev) =>
                        prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                      );
                    }
                  }}
                  selectClassName="input-field"
                  inputClassName="input-field"
                />
              </div>
            </div>
          </div>

          {/* Association */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Store className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Affectation</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Entreprise</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    value={formData.companyId}
                    onChange={(e) => set('companyId', e.target.value)}
                    className="input-field pl-9 appearance-none"
                  >
                    <option value="">Aucune</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Boutiques</label>
                <div className="relative">
                  <Store className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                  <select
                    value={formData.shopIds}
                    onChange={(e) => set('shopIds', Array.from(e.target.selectedOptions, (o) => o.value))}
                    className="input-field pl-9 min-h-[6rem]"
                    multiple
                  >
                    {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <p className="text-xs text-textSecondary mt-1">Ctrl+clic pour sélectionner plusieurs boutiques</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Rôle</label>
                <select
                  value={formData.type}
                  onChange={(e) => set('type', e.target.value as any)}
                  className="input-field"
                >
                  <option value="VENDOR">Vendeur / Réceptionniste</option>
                  <option value="SECRETARY">Secrétaire</option>
                  <option value="OTHER">Autre</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes & status */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <FileText className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Notes & statut</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  className="input-field"
                  placeholder="Informations supplémentaires sur l'employé"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-textPrimary cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => set('isActive', e.target.checked)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                Employé actif
              </label>
            </div>
          </div>
        </form>

        {/* User account section (outside the main form) */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <KeyRound className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Compte de connexion</h2>
          </div>

          {hasUserAccount ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">Compte actif</p>
                <p className="text-sm text-green-700">
                  Cet employé peut se connecter avec <strong>{linkedUserEmail}</strong>
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreateUser} autoComplete="off" className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Aucun compte de connexion</p>
                  <p className="text-sm text-amber-700">
                    Définissez un mot de passe pour permettre à cet employé de se connecter avec{' '}
                    <strong>{formData.email || '—'}</strong>.
                  </p>
                </div>
              </div>

              {createUserError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-medium">{createUserError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Mot de passe *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="input-field pl-9 pr-10"
                      placeholder="Minimum 6 caractères"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword
                        ? <EyeOff className="w-4 h-4 text-gray-400" />
                        : <Eye   className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Confirmer le mot de passe *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-field pl-9"
                      placeholder="Répétez le mot de passe"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={creatingUser || !newPassword}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {creatingUser ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span>Création…</span></>
                ) : (
                  <><KeyRound className="w-4 h-4" /><span>Créer le compte</span></>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
